"use client";

import type { ReactNode } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import type { AnyPermission } from "@/lib/auth/permissions";

type ProtectedProps = {
  children: ReactNode;
  fallback?: ReactNode;
  /** Rendered until permissions resolve, so access is never denied early. */
  loadingFallback?: ReactNode;
} & (
  | { permission: AnyPermission; anyOf?: never; allOf?: never }
  | { permission?: never; anyOf: AnyPermission[]; allOf?: never }
  | { permission?: never; anyOf?: never; allOf: AnyPermission[] }
);

export function Protected({
  children,
  permission,
  anyOf,
  allOf,
  fallback = null,
  loadingFallback = null,
}: ProtectedProps) {
  const { isLoading, hasPermission, hasAnyPermission, hasAllPermissions } =
    usePermissions();

  if (isLoading) return <>{loadingFallback}</>;

  const allowed = permission
    ? hasPermission(permission)
    : anyOf
      ? hasAnyPermission(anyOf)
      : hasAllPermissions(allOf);

  return allowed ? <>{children}</> : <>{fallback}</>;
}
